import { memo, useCallback, useMemo } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { AlertTriangle, Info, RotateCcw, Square } from 'lucide-react'
import type { ActivityStep, Message, MessageFeedback, Source, Verification } from '@/api/types'
import { extractCitedIds, trimPartialMarker } from '@/lib/citations'
import { closeOpenMarkdown } from '@/lib/streamingMarkdown'
import { chatController } from '@/stores/chatController'
import { isLocalId, useChatStore, type LiveAssistantMessage } from '@/stores/chatStore'
import { useUiStore } from '@/stores/uiStore'
import { ease, spring } from '@/lib/motion'
import { AiOrb } from '@/components/ui/AiOrb'
import { Button } from '@/components/ui/Button'
import { ActivitySteps } from './ActivitySteps'
import { CitationContext, type CitationContextValue } from './citationContext'
import { Markdown } from './Markdown'
import { MessageActions } from './MessageActions'
import { VerificationBadge } from './VerificationBadge'

type AssistantView = {
  serverId: string | null
  content: string
  sources: Source[]
  cited: string[]
  activity: ActivityStep[]
  status: 'streaming' | 'complete' | 'error' | 'stopped'
  statusText: string | null
  error: string | null
  verification: Verification | null
  feedback: MessageFeedback | null
  gapFlagged: boolean
}

function fromLive(a: LiveAssistantMessage): AssistantView {
  return {
    serverId: a.id,
    content: a.content,
    sources: a.sources,
    cited: a.cited ?? extractCitedIds(a.content, a.sources),
    activity: a.activity,
    status: a.status,
    statusText: a.statusText,
    error: a.error,
    verification: a.verification,
    feedback: a.feedback,
    gapFlagged: a.gap_flagged,
  }
}

function fromMessage(m: Message): AssistantView {
  const sources = m.sources ?? []
  return {
    serverId: isLocalId(m.id) ? null : m.id,
    content: m.content,
    sources,
    cited: m.cited ?? extractCitedIds(m.content, sources),
    activity: m.activity ?? [],
    status: m.status,
    statusText: null,
    error: m.error ?? null,
    verification: m.verification ?? null,
    feedback: m.feedback ?? null,
    gapFlagged: Boolean(m.gap_flagged),
  }
}

type Props = {
  /** Stable React key; also identifies the message for the sources panel. */
  rowKey: string
  message?: Message
  streamId?: string
  conversationId: string | null
  isLast: boolean
  /** False while another turn is streaming in this conversation. */
  canAct: boolean
  /** Fade in (answers started in this session). */
  animateIn?: boolean
}

export const AssistantMessage = memo(function AssistantMessage({
  rowKey,
  message,
  streamId,
  conversationId,
  isLast,
  canAct,
  animateIn = false,
}: Props) {
  const live = useChatStore((s) => (streamId ? s.turns[streamId]?.assistant : undefined))
  const verifying = useChatStore((s) => (message ? Boolean(s.pendingVerification[message.id]) : false))
  const openSources = useUiStore((s) => s.openSources)

  const view = useMemo(() => (live ? fromLive(live) : message ? fromMessage(message) : null), [live, message])
  const sources = view?.sources
  const onOpenSource = useCallback((sourceId: string) => openSources(rowKey, sourceId), [openSources, rowKey])
  const citationValue = useMemo<CitationContextValue>(
    () => ({ sourcesById: new Map((sources ?? []).map((s) => [s.id, s])), onOpen: onOpenSource }),
    [sources, onOpenSource],
  )

  if (!view) return null
  const streaming = view.status === 'streaming'
  const content = streaming ? closeOpenMarkdown(trimPartialMarker(view.content)) : view.content
  const department = () => useUiStore.getState().department
  const sourceCount = view.cited.length || view.sources.length

  return (
    <motion.article
      aria-label="Answer"
      className="group/answer min-w-0"
      initial={animateIn ? { opacity: 0, y: 8 } : false}
      animate={{ opacity: 1, y: 0 }}
      transition={spring.smooth}
    >
      {streaming && !content && <WorkingStatus text={view.statusText ?? 'Thinking…'} large />}

      {content && (
        <CitationContext.Provider value={citationValue}>
          <Markdown content={content} streaming={streaming} incremental />
        </CitationContext.Provider>
      )}

      {streaming && content && view.statusText && <WorkingStatus text={view.statusText} />}

      {view.status === 'stopped' && (
        <p className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-subtle px-2.5 py-1 text-xs font-medium text-ink-muted">
          <Square className="size-3 fill-current" aria-hidden />
          Stopped
        </p>
      )}

      {view.status === 'error' && (
        <motion.div
          role="alert"
          initial={{ opacity: 0, y: 6, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={spring.smooth}
          className="mt-3 flex gap-3 rounded-2xl bg-danger-soft p-4 text-sm shadow-[inset_0_0_0_0.5px_rgb(var(--danger)/0.3)]"
        >
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-danger" aria-hidden />
          <div className="min-w-0 flex-1">
            <p className="font-medium text-ink">Couldn&rsquo;t finish this answer</p>
            <p className="mt-1 text-ink-muted">{view.error || 'Something went wrong.'}</p>
            {isLast && (
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                disabled={!canAct}
                onClick={() => chatController.retry(conversationId, rowKey, department())}
              >
                <RotateCcw className="size-3.5" aria-hidden />
                Retry
              </Button>
            )}
          </div>
        </motion.div>
      )}

      {view.gapFlagged && !streaming && (
        <p className="mt-4 flex items-start gap-1.5 text-xs text-ink-muted motion-safe:animate-fade-up">
          <Info className="mt-px size-3.5 shrink-0 text-ink-faint" aria-hidden />
          This gap has been reported to the knowledge base owners.
        </p>
      )}

      {!streaming && view.status !== 'error' && (
        <VerificationBadge verification={view.verification} verifying={verifying} />
      )}

      {view.activity.length > 0 && <ActivitySteps steps={view.activity} streaming={streaming} />}

      {!streaming && view.status !== 'error' && (
        <motion.div
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...spring.smooth, delay: 0.05 }}
        >
          <MessageActions
            content={view.content}
            messageId={view.serverId}
            conversationId={conversationId}
            feedback={view.feedback}
            sourceCount={sourceCount}
            canRegenerate={isLast && canAct && Boolean(conversationId)}
            onRegenerate={() => {
              if (conversationId) chatController.regenerate(conversationId, department())
            }}
            onShowSources={() => openSources(rowKey, view.cited[0] ?? null)}
          />
        </motion.div>
      )}
    </motion.article>
  )
})

/** "Searching the knowledge base…": spinning orb with a light sweep over the text. */
function WorkingStatus({ text, large = false }: { text: string; large?: boolean }) {
  return (
    <div className={large ? 'flex h-8 items-center gap-3' : 'mt-3 flex h-5 items-center gap-2'} aria-hidden>
      <AiOrb className={large ? 'size-[18px]' : 'size-3'} />
      <span className="relative min-w-0 flex-1">
        <AnimatePresence mode="popLayout" initial={false}>
          <motion.span
            key={text}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.3, ease: ease.out }}
            className={
              large
                ? 'shimmer-text block truncate text-[14.5px] font-medium'
                : 'shimmer-text block truncate text-xs font-medium'
            }
          >
            {text}
          </motion.span>
        </AnimatePresence>
      </span>
    </div>
  )
}
